import { makeStyles, MessageBar, MessageBarBody, tokens } from '@fluentui/react-components';
import { QueryAggregate } from '@powerportalspro/core';
import { AuthStatus, useAuth, useT } from '@powerportalspro/react';
import { KanbanBoard, type ViewDefinition } from '@powerportalspro/react-fluent';
import { useMemo } from 'react';
import { DemoShowcaseHeading, DemoShowcaseLayout } from './DemoShowcaseLayout';
import { companiesIn, MIDWEST, NORTHEAST, SOUTHEAST, SOUTHWEST, WEST, type DemoRegion } from './DemoRegion';

// A sales pipeline: opportunities in a lane for each status reason, from In Progress and On Hold to
// Won, Canceled and Out-Sold, through views the page defines: the whole pipeline, or that of one
// region's companies. The sample opportunities stay where they are: visitors can't change them. A
// signed-in visitor adds opportunities of their own with an open lane's Add button, moves them, and
// closes them by dropping them on Won, Canceled or Out-Sold, where the board asks how.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Demos.KanbanBoardShowcase.KanbanBoardPipelineMainDemo';

const PIPELINE_VIEW_ID = '4c1e7a92-5d3b-4f8e-9a6c-2b0d8e4f1a37';
const MY_OPPORTUNITIES_VIEW_ID = '4c1e7a92-5d3b-4f8e-9a6c-2b0d8e4f1a38';

// A view of the pipeline of each region's companies, beside the whole pipeline.
const REGION_VIEWS: { region: DemoRegion; viewId: string }[] = [
  { region: WEST, viewId: 'bbb85ba0-8d08-494e-8d4f-ba2eac31c92a' },
  { region: SOUTHWEST, viewId: '339ddc5f-bb71-4175-bc38-ad314167fdf1' },
  { region: MIDWEST, viewId: 'a6753e77-1f61-48e2-92e0-4bf9f04bb176' },
  { region: SOUTHEAST, viewId: '12201bec-2f45-4a61-b15c-1da3cdd1a763' },
  { region: NORTHEAST, viewId: '363dfbd8-28b9-4dc6-ad33-5c22790b10a1' },
];

// A view laying the board out itself: ten cards a lane, and each lane's estimated revenue in its
// header. The pipeline is the sample opportunities, open and closed, and, for a signed-in visitor,
// their own in any state, whole or of one region's companies; their own alone is another view. The
// pipeline's sample opportunities are those the demo ranks, in any state, rather than the thousands of
// older closed ones behind the charts.
function view(
  id: string,
  displayName: string,
  userId: string | null,
  includeSamples: boolean,
  region: DemoRegion | null = null,
): ViewDefinition {
  const samples = includeSamples ? '<filter><condition attribute="ppp_owningportaluserid" operator="null" /><condition attribute="ppp_kanbanrank" operator="not-null" /></filter>' : '';
  const own = userId ? `<condition attribute="ppp_owningportaluserid" operator="eq" value="${userId}" />` : '';
  return {
    id,
    tableName: 'opportunity',
    displayName,
    pageSize: 10,
    fetchXml: `
      <fetch>
        <entity name="opportunity">
          <attribute name="name" />
          <attribute name="estimatedvalue" />
          <attribute name="estimatedclosedate" />
          <order attribute="name" />
          <filter type="or">
            ${samples}
            ${own}
          </filter>
          ${region ? companiesIn(region, 'parentaccountid') : ''}
        </entity>
      </fetch>`,
    columns: [
      { columnName: 'name' },
      { columnName: 'estimatedvalue', aggregates: [{ aggregate: QueryAggregate.Sum, format: 'C0' }] },
      { columnName: 'estimatedclosedate' },
    ],
  };
}

const useStyles = makeStyles({
  list: {
    margin: 0,
    paddingInlineStart: tokens.spacingHorizontalXL,
  },
});

// The strings are HTML, written once for both stacks.
function Item({ text }: { text: string }) {
  return <li dangerouslySetInnerHTML={{ __html: text }} />;
}

export function KanbanBoardPipelineMainSample() {
  const styles = useStyles();
  const t = useT();
  const auth = useAuth();
  const userId = auth.status === AuthStatus.Authenticated ? (auth.user.userId ?? null) : null;
  const signedIn = auth.status === AuthStatus.Authenticated;

  // Built once for the user, as a new list starts the board again.
  const views = useMemo<ViewDefinition[]>(
    () => [
      view(PIPELINE_VIEW_ID, t(`${STRINGS_BASE}.view-pipeline`), userId, true),
      ...REGION_VIEWS.map(({ region, viewId }) => view(viewId, t(`${STRINGS_BASE}.view-${region.key}`), userId, true, region)),
      ...(userId ? [view(MY_OPPORTUNITIES_VIEW_ID, t(`${STRINGS_BASE}.view-mine`), userId, false)] : []),
    ],
    [t, userId],
  );

  return (
    <DemoShowcaseLayout
      title={t(`${STRINGS_BASE}.try-title`)}
      tips={
        <>
          <MessageBar intent="info">
            <MessageBarBody>{t(`${STRINGS_BASE}.${signedIn ? 'moves-saved' : 'sign-in-to-add'}`)}</MessageBarBody>
          </MessageBar>

          <ul className={styles.list}>
            <Item text={t(`${STRINGS_BASE}.try-add`)} />
            <Item text={t(`${STRINGS_BASE}.try-win`)} />
            <Item text={t(`${STRINGS_BASE}.try-lose`)} />
            <Item text={t(`${STRINGS_BASE}.try-cancel`)} />
            <Item text={t(`${STRINGS_BASE}.try-closed`)} />
            <Item text={t(`${STRINGS_BASE}.try-order`)} />
            <Item text={t(`${STRINGS_BASE}.try-views`)} />
          </ul>

          <DemoShowcaseHeading>{t(`${STRINGS_BASE}.rules-title`)}</DemoShowcaseHeading>
          <ul className={styles.list}>
            <Item text={t(`${STRINGS_BASE}.rule-access`)} />
            <Item text={t(`${STRINGS_BASE}.rule-close`)} />
          </ul>
        </>
      }
    >
      {/* Drawn once the user is known, so the board doesn't read the pipeline first and then the
          signed-in visitor's own. */}
      {auth.status !== AuthStatus.Loading && (
        <KanbanBoard
          customViewDefinitions={views}
          defaultViewId={userId ? MY_OPPORTUNITIES_VIEW_ID : PIPELINE_VIEW_ID}
          laneColumn="statuscode"
          rankColumn="ppp_kanbanrank"
          editable
          allowFiltering
          persistedStateQueryParameter="boardState"
          height="760px"
        />
      )}
    </DemoShowcaseLayout>
  );
}
